import {
  CellRange,
  ColDef,
  Column,
  ColumnChooserParams,
  ColumnState,
  ColumnVisibleEvent,
  GetRowIdParams,
  GridApi,
  IRowNode,
  PostProcessPopupParams,
  ProcessCellForExportParams,
  ProcessDataFromClipboardParams,
  RowClassParams,
  SortComparatorFn,
} from 'ag-grid-community';
import { addYears, endOfDay, lastDayOfMonth } from 'date-fns';
import { get, omit, set } from 'lodash-es';
import { FieldNameRenderer } from '../../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import {
  ICompanyFinancialsDataModel,
  isKpiPeriod,
  KpiPeriod,
  KpiPeriodExtended,
  KpiSection,
} from '../../../../../data-models/company-financials.data-model';
import { RendererType } from '../../../../../data-models/field-definition';
import { FieldDefinition } from '../../../../../data-models/field-definition/field-definition.schema';
import { field2ToFormField, IField, isNumericRenderer } from '../../../../../data-models/field2.data-model';
import { getGridFormatterForFormField } from '../../../../../data-models/field3.data-model';
import {
  KpiPeriodToKpiRequestPeriod,
  periodToFrequency,
} from '../../../../../data-models/kpi-requests.data-model';
import { KPIGroup } from '../../../../../schemas/kpi-group.schema';
import { nonEditableCellClass, nonEditableRowClass } from '../../../../../theme/gridStyle';
import { compareBySortOrderThenName } from '../../../../../util/comparators';
import {
  dateToPlainDateString,
  isInvalidDate,
  plainDateToZonedDateTime,
} from '../../../../../util/date-utilts';
import { FMT } from '../../../../../util/formatter-service';
import { getFiscalYear } from '../../../../../util/formatters/DateFormatters';
import { ParserService, StandardParserId } from '../../../../../util/parser-service';
import { IFormField } from '../../../../../view-models/form.view-model';
import { numericCellClasses } from '../../../../Finance2/common-grid-defs/commonColDefs';
import { KPICellEditor } from '../../../../KPI/components/KPIRequests/KPIRequestResponse/KPIAgGrid';
import { generateKPITableColHeaderData, KPITableColHeaderData } from '../../../../KPI/utils';
import { getEachDateByPeriodAndFrequency } from '../../../utils/financialUtils';
import { financialToTypedFinancial } from '../../../utils/getTypedFinancials';
import { kpiNameComparator } from '../hooks/useFinancialsColumnDefs';
import { getCurrencyAwareFormatterModel } from '../utils/getCurrencyAwareFormatterModel';
import { calculatedDescription } from './KpiEditor';
import {
  FinancialsKpiNameRenderer,
  FinancialsValueRenderer,
  PinnedYearsChooserHeader,
} from './FinancialsGrid2Renderers';

type GrowthPeriod = Extract<KpiPeriodExtended, 'GrowthMonth' | 'GrowthQuarter' | 'GrowthYear'>;
/** The kinds of rows a KPI stack can hold: sections of the selected period, plus derived rows. */
export type FinancialsRowKind = KpiSection | Extract<KpiPeriodExtended, 'CYTD' | 'LTM'>;
export type FinancialsGridRowData = Pick<ICompanyFinancialsDataModel, 'kpiId' | 'period' | 'section'> & {
  kind: FinancialsRowKind;
  kpiGroup: string;
  kpiGroupId: number | null;
  kpiDisplayName: string;
  kpiName: string;
  formField: IFormField<unknown>;
} & Record<string, unknown>;
/** The fiscal year ends a pinned column can show, relative to today; index 1 and 2 are the previous and current FY. */
const PINNED_YEAR_OFFSETS = [-2, -1, 0, 1, 2];
const PINNED_YEAR_FIELD_PREFIX = `${KpiPeriod.year}:`;

/** The row-data key of a pinned fiscal-year column (`Year:<fy end>`), beside the selected period's own key. */
export function pinnedYearField(isoDate: string) {
  return `${PINNED_YEAR_FIELD_PREFIX}${isoDate}`;
}

function isPinnedYearField(field: string | undefined) {
  return field?.startsWith(PINNED_YEAR_FIELD_PREFIX) === true;
}

/** The five fiscal year ends a pinned column can show, FY-2 to FY+2 around `now` (legacy `getPinnedYearColumnDates`). */
export function getPinnedYearEndDates(fye: number /* 1-12 */, now: Date): string[] {
  return PINNED_YEAR_OFFSETS.map((offset) => getFiscalYear(addYears(now, offset), fye).plainDate.toString());
}

export interface IFinancialsCellKey {
  period: KpiPeriod | KpiPeriodExtended;
  date: string;
}

/**
 * The financial a cell shows. A bare date belongs to the column of the selected period, so the row answers for it;
 * a prefixed key names its own period. A derived row keeps its kind in a pinned column: YTD has an item at the
 * fiscal year end, LTM has none and stays blank, as in the legacy table.
 */
export function resolveCellKey(
  field: string | undefined,
  row: Pick<FinancialsGridRowData, 'period'> | undefined
): IFinancialsCellKey | null {
  if (!field || !row) {
    return null;
  }
  const separator = field.indexOf(':');
  if (separator === -1) {
    return { period: row.period, date: field };
  }
  const period = field.slice(0, separator);
  const date = field.slice(separator + 1);
  if (isKpiPeriod(row.period)) {
    return { period: isKpiPeriod(period) ? period : row.period, date };
  }
  return row.period === 'CYTD' ? { period: row.period, date } : null;
}

/** The row a cell's financial belongs to: this row's KPI and section, at the period its column names. */
export function cellRow(row: FinancialsGridRowData, key: IFinancialsCellKey) {
  return { kpiId: row.kpiId, period: key.period, section: row.section };
}

/**
 * One row per (kpi, period, section), values keyed by plain date, plus a `Year:<date>` key per pinned
 * fiscal-year column asked for.
 * Per KPI the rows come out in stack order actual, budget, LTM, CYTD; LTM and CYTD rows carry the actual section only.
 */
export function financialsToRowData({
  financials,
  include,
  kpiMap,
  kpiGroupMap,
  period,
  pinnedYears,
  readOnly = false,
}: {
  financials: ICompanyFinancialsDataModel[];
  include: Set<FinancialsRowKind>;
  kpiMap: Map<number, IField<unknown>>;
  kpiGroupMap: Map<number, KPIGroup>;
  period: KpiPeriod;
  /** The fiscal-year columns to fill, with the index the caller already holds; omitted when none are pinned. */
  pinnedYears?: { dates: string[]; financialsByKey: Record<string, unknown> };
  readOnly: boolean;
}): FinancialsGridRowData[] {
  const rows = kpisForEachSection({
    kpis: Array.from(kpiMap.values()),
    kpiGroupMap,
    include,
    period,
    readOnly,
  });

  const rowIndexByKpiIdPeriodSection = rows.reduce((acc, row, i) => {
    set(acc, [row.kpiId, row.period, row.section], i);
    return acc;
  }, {});

  financials
    .filter((item) => doesKpiFilterPass(item, period, include))
    .forEach((kpiItem) => {
      const kpi = kpiMap.get(kpiItem.kpiId);
      if (!kpi) {
        return;
      }
      const { kpiId, period, section } = kpiItem;
      const rowIndex: number | undefined = get(rowIndexByKpiIdPeriodSection, [kpiId, period, section]);
      if (rowIndex === undefined) {
        return;
      }
      rows[rowIndex] = {
        ...rows[rowIndex],
        [kpiItem.date]: financialToTypedFinancial(kpiItem, kpiMap)?.value,
      };
    });
  return pinnedYears ? fillPinnedYearValues({ rows, pinnedYears, kpiMap }) : rows;
}

/** Each pinned fiscal-year column takes the row's own kind at that year end, whatever period the grid shows. */
function fillPinnedYearValues({
  rows,
  pinnedYears,
  kpiMap,
}: {
  rows: FinancialsGridRowData[];
  pinnedYears: { dates: string[]; financialsByKey: Record<string, unknown> };
  kpiMap: Map<number, IField<unknown>>;
}): FinancialsGridRowData[] {
  const { dates, financialsByKey } = pinnedYears;
  if (dates.length === 0) {
    return rows;
  }
  return rows.map((row) => {
    const values = dates.reduce<Record<string, unknown>>((acc, date) => {
      const field = pinnedYearField(date);
      const key = resolveCellKey(field, row);
      const item = key ? getFinancialAt(financialsByKey, cellRow(row, key), key.date) : undefined;
      if (item) {
        acc[field] = financialToTypedFinancial(item, kpiMap)?.value;
      }
      return acc;
    }, {});
    return Object.keys(values).length > 0 ? { ...row, ...values } : row;
  });
}

export function getFinancialsRowId(params: GetRowIdParams) {
  if (!params.data) {
    return '';
  }
  return `${params.data.kpiId}_${params.data.period}_${params.data.section}`;
}

export interface IFinancialsGridDatesParams {
  nSelectedPeriods: number;
  period: KpiPeriod;
  fye: number;
  endDate?: Date | null;
}

/**
 * Column keys for the financials grids: the end-of-period dates as local plain dates
 * (`YYYY-MM-DD`), matching the plain dates the financials atom stores on every item.
 */
export function getFinancialsGridIsoDates({
  nSelectedPeriods,
  period,
  fye,
  endDate,
}: IFinancialsGridDatesParams): string[] {
  return getEachDateByPeriodAndFrequency({
    totalSelectedPeriods: nSelectedPeriods,
    frequency: period,
    fye,
    endDate: endDate ?? undefined,
  }).dates.map(dateToPlainDateString);
}

type FinancialsHeaderData = KPITableColHeaderData & Partial<ColDef>;
// same header data format as in KPIAgGrid so we can append request request header data to this and pass the whole thing to col def getter
export function getFinancialsHeaderData(
  fye: number, // 1-12
  isoDates: string[],
  period: KpiPeriod,
  overrides: Partial<ColDef> = {}
): FinancialsHeaderData[] {
  return isoDates.reduce((acc, isoDate) => {
    acc.push(
      ...generateKPITableColHeaderData({
        fyeDateString: dateToPlainDateString(lastDayOfMonth(new Date(new Date().getFullYear(), fye - 1, 2))),
        periodISODate: isoDate,
        selectedFrequency: KpiPeriodToKpiRequestPeriod[period],
        kpiRequestFrequency: periodToFrequency[period],
      }).map((header) => ({ ...header, ...overrides }))
    );
    return acc;
  }, [] as FinancialsHeaderData[]);
}

/**
 * Who may edit a financials grid, in three layers: the grid as a whole, then the column (a header's own
 * `editable: false`), then the cell. Each layer is only asked once the one above it has agreed, so a grid
 * that is not editable never consults a cell rule. Each grid brings its own rule.
 */
export interface IFinancialsEditPolicy {
  /** The grid-level gate: false makes every cell of the grid read-only. */
  enabled: boolean;
  /** The cell's own rule. `field` is its row-data key, which names the cell's period as well as its date. */
  isCellEditable: (row: FinancialsGridRowData, field: string) => boolean;
}

/**
 * Whether a cell belongs to a row of the grid's own data. A group band is a node ag-grid builds itself and
 * never gives data to, so it has no KPI to edit; a cell with no value still sits on a row and does.
 */
function isDataRow(data: FinancialsGridRowData | undefined): data is FinancialsGridRowData {
  return data != null;
}

/** One column's `editable` callback under a policy. */
function financialsEditableCallback(edit: IFinancialsEditPolicy, field: string) {
  return ({ data }: { data?: FinancialsGridRowData }) =>
    edit.enabled && isDataRow(data) && edit.isCellEditable(data, field);
}

/** The KPI request grid's rule: a cell of the row's own period, unless the BE calculated its value. */
export function kpiRequestCellEditable(financialsByKpiIdPeriodSectionDate: Record<string, unknown>) {
  return (row: FinancialsGridRowData, field: string) =>
    isKpiPeriod(row.period) &&
    getFinancialAt(financialsByKpiIdPeriodSectionDate, row, field)?.calculated !== true;
}

export function getFinancialsColumnDefs(
  headerData: FinancialsHeaderData[],
  edit: IFinancialsEditPolicy
): ColDef<FinancialsGridRowData>[] {
  return headerData.reduce((acc, header) => {
    const overrides = omit(header, ['date', 'title', 'editable']);
    // a header's own `editable: false` is the column refusing, whatever the grid's policy would allow
    const isEditable = financialsEditableCallback(
      header.editable === false ? { ...edit, enabled: false } : edit,
      header.field ?? header.date
    );
    acc.push({
      cellDataType: false,
      cellClass: (params) =>
        isEditable(params) ? numericCellClasses : [nonEditableCellClass, ...numericCellClasses],
      cellEditor: KPICellEditor,
      editable: isEditable,
      field: header.date,
      headerName: header.title,
      minWidth: 100,
      suppressHeaderMenuButton: true,
      valueFormatter: (params) => {
        if (!params.data?.formField) {
          return params.value;
        }
        const formatter = getGridFormatterForFormField(params.data.formField);
        return formatter(params.value) ?? params.value;
      },
      valueParser: (params) => {
        const rendererType = params.data?.formField?.renderer;
        if (rendererType && isNumericRenderer(rendererType)) {
          return ParserService.get().parse(StandardParserId.Number, params.newValue);
        }
        return params.newValue;
      },
      ...overrides,
    });
    return acc;
  }, [] as ColDef<FinancialsGridRowData>[]);
}

function doesKpiFilterPass(
  kpiItem: Pick<FinancialsGridRowData, 'period' | 'section'>,
  period: KpiPeriod,
  include: Set<FinancialsRowKind>
) {
  if (isKpiPeriod(kpiItem.period)) {
    return kpiItem.period === period && include.has(kpiItem.section);
  }
  if (kpiItem.section !== KpiSection.actual) {
    return false;
  }
  if (kpiItem.period === 'CYTD' || kpiItem.period === 'LTM') {
    return include.has(kpiItem.period);
  }
  return false;
}

export const periodToGrowth: Record<KpiPeriod, GrowthPeriod> = {
  [KpiPeriod.month]: 'GrowthMonth',
  [KpiPeriod.quarter]: 'GrowthQuarter',
  [KpiPeriod.year]: 'GrowthYear',
} as const;

function getKpiName(displayName: string, kind: FinancialsRowKind) {
  return `${displayName} (${kind})`;
}

function kpisForEachSection({
  kpis,
  kpiGroupMap,
  include,
  period,
  readOnly,
}: {
  kpis: IField<unknown>[];
  kpiGroupMap: Map<number, KPIGroup>;
  include: Set<FinancialsRowKind>;
  period: KpiPeriod;
  readOnly: boolean;
}) {
  return kpis.flatMap((kpi) => {
    const toRow = (
      rowPeriod: KpiPeriod | KpiPeriodExtended,
      section: KpiSection,
      kind: FinancialsRowKind
    ): FinancialsGridRowData => ({
      formField: {
        ...field2ToFormField(kpi),
        autoFocus: true,
        disabled: readOnly,
        fullWidth: true,
      },
      kind,
      kpiId: kpi.id,
      section,
      period: rowPeriod,
      kpiDisplayName: kpi.displayName,
      kpiGroup: kpiGroupMap.get(kpi.kpiGroupId ?? -1)?.name ?? '',
      kpiGroupId: kpi.kpiGroupId ?? null,
      kpiName: getKpiName(kpi.displayName, kind),
    });
    const rows: FinancialsGridRowData[] = [];
    Object.values(KpiSection).forEach((section) => {
      if (include.has(section)) {
        rows.push(toRow(period, section, section));
      }
    });
    (['LTM', 'CYTD'] as const).forEach((derivedPeriod) => {
      if (include.has(derivedPeriod)) {
        rows.push(toRow(derivedPeriod, KpiSection.actual, derivedPeriod));
      }
    });
    return rows;
  });
}

export const kpiNameColumnDefs: ColDef<FinancialsGridRowData> = {
  cellRenderer: FieldNameRenderer,
  cellRendererParams: {
    namePath: 'kpiName',
    descriptionPath: 'formField.description',
  },
  cellStyle: { textAlign: 'left' },
  colId: 'kpiName',
  field: 'kpiName',
  filter: true,
  filterParams: {
    buttons: ['reset'],
  },
  headerName: 'KPI',
  initialSort: 'asc',
  pinned: 'left',
  sortable: true,
  tooltipField: 'kpiName',
  width: 250,
  maxWidth: 350,
};

/** Looks a financial up in the grid-level index `kpiId → period → section → plain date`. */
export function getFinancialAt(
  financialsByKpiIdPeriodSectionDate: Record<string, unknown>,
  row: Pick<FinancialsGridRowData, 'kpiId' | 'period' | 'section'>,
  date: string
): ICompanyFinancialsDataModel | undefined {
  return get(financialsByKpiIdPeriodSectionDate, [row.kpiId, row.period, row.section, date]);
}

export function getFinancialsRowClass(params: RowClassParams<FinancialsGridRowData>) {
  if (!params.data) {
    return '';
  }
  // derived rows (LTM, CYTD) are read-only as a whole; standard rows decide per cell
  if (!isKpiPeriod(params.data.period)) {
    return nonEditableRowClass;
  }
  return '';
}

// ----------------------------------------------------------------------------------------------
// Company Profile › Financials tab only. KPIAgGrid2 uses the shared defs above, never these.
// ----------------------------------------------------------------------------------------------

/** Tint classes for the pinned fiscal-year area; `FinancialsGrid2` styles them. */
export const PINNED_YEAR_CELL_CLASS = 'pinned-year-cell';
export const PINNED_YEAR_HEADER_CLASS = 'pinned-year-header';
/** The previous and the current fiscal year, as in the legacy table. */
export const DEFAULT_PINNED_YEAR_INDICES = [1, 2];

const PINNED_YEAR_COL_ID_PREFIX = 'pinnedYear_';

export function pinnedYearColId(index: number) {
  return `${PINNED_YEAR_COL_ID_PREFIX}${index}`;
}

function pinnedYearIndex(colId: string): number | null {
  if (!colId.startsWith(PINNED_YEAR_COL_ID_PREFIX)) {
    return null;
  }
  const index = Number(colId.slice(PINNED_YEAR_COL_ID_PREFIX.length));
  return Number.isInteger(index) ? index : null;
}

function isPinnedYearColId(colId: string) {
  return pinnedYearIndex(colId) !== null;
}

/**
 * The pinned fiscal-year columns, in the same shape as the period ones so they run through the same defs.
 * `initialHide` seeds the selection once, when the columns are created; from then on their visibility is the
 * grid's own, which a `hide` here would overwrite on every rebuild.
 */
export function getPinnedYearHeaderData({
  fye,
  dates,
  selectedIndices,
}: {
  fye: number; // 1-12
  dates: string[];
  selectedIndices: number[];
}): FinancialsHeaderData[] {
  const selected = new Set(selectedIndices);
  return dates.flatMap((date, index) =>
    getFinancialsHeaderData(fye, [date], KpiPeriod.year, {
      colId: pinnedYearColId(index),
      field: pinnedYearField(date),
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      suppressMovable: true,
      initialHide: !selected.has(index),
      headerClass: PINNED_YEAR_HEADER_CLASS,
    })
  );
}

const ADD_PINNED_YEARS_COL_ID = 'addPinnedYears';

const pinnedYearsChooserParams: ColumnChooserParams = {
  // a layout of its own is what keeps the list to the pinned years: it also turns off syncing with the grid
  columnLayout: PINNED_YEAR_OFFSETS.map((_offset, index) => ({ colId: pinnedYearColId(index) })),
  suppressColumnFilter: true,
  suppressColumnSelectAll: true,
  suppressColumnExpandAll: true,
};

/**
 * The trailing "+" column, whose header opens the chooser. It is kept out of every menu of its own, so no one
 * can hide it and leave the pinned years unreachable. Constant, so its header is not rebuilt with the columns.
 */
export const addPinnedYearsColDef: ColDef<FinancialsGridRowData> = {
  colId: ADD_PINNED_YEARS_COL_ID,
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  pinned: 'right',
  lockPinned: true,
  lockPosition: 'right',
  suppressMovable: true,
  resizable: false,
  suppressNavigable: true,
  suppressPaste: true,
  suppressHeaderContextMenu: true,
  suppressColumnsToolPanel: true,
  enableRowGroup: false,
  headerClass: PINNED_YEAR_HEADER_CLASS,
  cellClass: PINNED_YEAR_CELL_CLASS,
  headerComponent: PinnedYearsChooserHeader,
  headerComponentParams: { chooserParams: pinnedYearsChooserParams },
};

/** Right edge under the anchor, top just below it, both kept inside the grid root, which clips what overflows. */
export function placePopupBelowAnchor({
  popup,
  anchor,
  parent,
}: {
  popup: { width: number; height: number };
  anchor: Pick<DOMRect, 'right' | 'bottom'>;
  parent: Pick<DOMRect, 'left' | 'top' | 'height'>;
}) {
  return {
    left: Math.max(0, anchor.right - parent.left - popup.width),
    top: Math.max(0, Math.min(anchor.bottom - parent.top, parent.height - popup.height)),
  };
}

/**
 * Puts the chooser the "+" opened under its button rather than in the middle of the grid. Only that one: a
 * chooser opened from a column's own menu carries that column and stays where ag-grid placed it.
 */
export function anchorColumnChooserToAddButton(params: PostProcessPopupParams<FinancialsGridRowData>) {
  if (params.type !== 'columnChooser' || params.column) {
    return;
  }
  const root = params.ePopup.closest('.ag-root-wrapper');
  const button = root?.querySelector(`.ag-header-cell[col-id="${ADD_PINNED_YEARS_COL_ID}"]`);
  if (!root || !button) {
    return;
  }
  const { left, top } = placePopupBelowAnchor({
    popup: { width: params.ePopup.offsetWidth, height: params.ePopup.offsetHeight },
    anchor: button.getBoundingClientRect(),
    parent: root.getBoundingClientRect(),
  });
  params.ePopup.style.left = `${left}px`;
  params.ePopup.style.top = `${top}px`;
}

/** The pinned years the grid is showing, ascending: what the seed keeps while the columns are away. */
function selectedPinnedYearIndices(columnState: ColumnState[]): number[] {
  return columnState
    .flatMap((state) => {
      const index = pinnedYearIndex(state.colId);
      return index !== null && !state.hide ? [index] : [];
    })
    .sort((a, b) => a - b);
}

/**
 * The years to seed from a visibility event; null when the event is about other columns. The event's source is
 * deliberately not filtered: nothing here pushes state into the grid, so there is no echo to screen out, and
 * the seed is read back from the whole column state, which a stray event would leave as it found it.
 */
export function pinnedYearSelectionFromEvent(event: ColumnVisibleEvent): number[] | null {
  if (!event.columns?.some((column) => isPinnedYearColId(column.getColId()))) {
    return null;
  }
  return selectedPinnedYearIndices(event.api.getColumnState());
}

export interface IFinancialsSortMaps {
  kpisByName: Map<string, FieldDefinition>;
  kpiGroupsById: Map<number, KPIGroup>;
}

/** Only `data`, `allLeafChildren` and `key` are read, so a bare `{ data }` sorts like a grid row node. */
type RowNodeLike = Pick<IRowNode<FinancialsGridRowData>, 'data'> & {
  allLeafChildren?: Pick<IRowNode<FinancialsGridRowData>, 'data'>[] | null;
  key?: string | null;
};

const stackOrder: FinancialsRowKind[] = [KpiSection.actual, KpiSection.budget, 'LTM', 'CYTD'];

interface IKpiGroupLookup {
  byId: Map<number, KPIGroup>;
  byName: Map<string, KPIGroup>;
}

function kpiGroupLookup(kpiGroupsById: Map<number, KPIGroup>): IKpiGroupLookup {
  return {
    byId: kpiGroupsById,
    byName: new Map(Array.from(kpiGroupsById.values(), (group) => [group.name, group])),
  };
}

function kpiGroupOf(groups: IKpiGroupLookup, node: RowNodeLike): KPIGroup | undefined {
  const data = node.allLeafChildren?.[0]?.data ?? node.data;
  if (data) {
    return data.kpiGroupId === null ? undefined : groups.byId.get(data.kpiGroupId);
  }
  // only a group row whose leaves ag-grid has not loaded reaches here, and its key is the group name; a row of
  // its own answers with its kind or its KPI id (getFinancialsDataPath), neither of which names a group
  return node.key ? groups.byName.get(node.key) : undefined;
}

/** Groups follow the admin-defined sortOrder, ties (every group starts at 0) their name; ungrouped last. */
function compareByKpiGroup(groups: IKpiGroupLookup, nodeA: RowNodeLike, nodeB: RowNodeLike) {
  return compareBySortOrderThenName(kpiGroupOf(groups, nodeA) ?? {}, kpiGroupOf(groups, nodeB) ?? {});
}

/**
 * Group order, then KPI sortOrder (legacy `kpiNameComparator`) and display name, then the fixed stack
 * actual → budget → LTM → CYTD. ag-grid negates the result for a descending sort: groups and KPIs flip,
 * the stack part is pre-negated so it stays.
 */
export function compareFinancialsRows({ kpisByName, kpiGroupsById }: IFinancialsSortMaps) {
  const groups = kpiGroupLookup(kpiGroupsById);
  return (nodeA: RowNodeLike, nodeB: RowNodeLike, isDescending = false): number => {
    const byGroup = compareByKpiGroup(groups, nodeA, nodeB);
    if (byGroup !== 0) {
      return byGroup;
    }
    const a = nodeA.data;
    const b = nodeB.data;
    if (!a || !b) {
      return 0;
    }
    const byKpi =
      kpiNameComparator(a.kpiDisplayName, b.kpiDisplayName, kpisByName) ||
      a.kpiDisplayName.localeCompare(b.kpiDisplayName, undefined, { sensitivity: 'base' });
    if (byKpi !== 0) {
      return byKpi;
    }
    const byStack = stackOrder.indexOf(a.kind) - stackOrder.indexOf(b.kind);
    return isDescending ? -byStack : byStack;
  };
}

function financialsKpiStackComparator(maps: IFinancialsSortMaps): SortComparatorFn<FinancialsGridRowData> {
  const compare = compareFinancialsRows(maps);
  return (_valueA, _valueB, nodeA, nodeB, isDescending) => compare(nodeA, nodeB, isDescending);
}

/** Row data in grid order, so the rows read the same when no column sort is applied. */
export function sortFinancialsRowData(rows: FinancialsGridRowData[], maps: IFinancialsSortMaps) {
  const compare = compareFinancialsRows(maps);
  return [...rows].sort((a, b) => compare({ data: a }, { data: b }));
}

const UNGROUPED_KPI_GROUP = '-';

/** Tree path: group → KPI (its actual row) → the other kinds of the stack, so a KPI reads as one block. */
export function getFinancialsDataPath(row: FinancialsGridRowData): string[] {
  const kpiPath = [row.kpiGroup || UNGROUPED_KPI_GROUP, String(row.kpiId)];
  return row.kind === KpiSection.actual ? kpiPath : [...kpiPath, row.kind];
}

const stackLabel: Record<Exclude<FinancialsRowKind, 'actual'>, string> = {
  budget: 'Budget',
  LTM: 'LTM',
  CYTD: 'YTD',
};

function kpiTreeCellValue(
  data: FinancialsGridRowData | undefined,
  node: Pick<IRowNode<FinancialsGridRowData>, 'key'> | null
): string {
  if (!data) {
    return node?.key ?? '';
  }
  return data.kind === KpiSection.actual ? data.kpiDisplayName : stackLabel[data.kind];
}

/**
 * The tree's auto group column; ag-grid merges it over its group cell renderer def, so the KPI renderer goes in as
 * `innerRenderer` (a `cellRenderer` would replace the chevron). The value is what the cell shows, for copy and filter.
 */
export function getCompanyProfileKpiTreeColDef(maps: IFinancialsSortMaps): ColDef<FinancialsGridRowData> {
  return {
    headerName: 'KPI',
    pinned: 'left',
    // this column keeps its header menu for the filter, and that menu would otherwise offer "No Pin";
    // every row is a KPI, so the grid says nothing once the names scroll away
    lockPinned: true,
    sortable: true,
    initialSort: 'asc',
    // no unsorted state from the header: the stack comparator always governs the order
    sortingOrder: ['asc', 'desc'],
    filter: true,
    // the tab's default col def hides the header buttons; this column needs them for the filter
    suppressHeaderMenuButton: false,
    suppressHeaderFilterButton: false,
    suppressHeaderContextMenu: false,
    // the default col def enables row grouping, which does nothing under tree data
    enableRowGroup: false,
    filterParams: {
      buttons: ['reset'],
      comparator: (valueA?: string, valueB?: string) =>
        valueA?.localeCompare(valueB ?? '', undefined, { sensitivity: 'base' }) ?? 0,
    },
    minWidth: 250,
    width: 250,
    tooltipField: 'kpiName',
    cellStyle: { textAlign: 'left' },
    // the kind labels sit at the right edge as in the legacy grid; ag-grid's class pulls the group value right
    cellClass: (params) =>
      params.data && params.data.kind !== KpiSection.actual ? 'ag-right-aligned-cell' : undefined,
    valueGetter: (params) => kpiTreeCellValue(params.data, params.node),
    // one entry per KPI in the set filter; a group row yields nothing, so only its KPIs decide whether it shows
    filterValueGetter: (params) => params.data?.kpiDisplayName,
    cellRendererParams: { innerRenderer: FinancialsKpiNameRenderer, suppressCount: true },
    comparator: financialsKpiStackComparator(maps),
  };
}

function isRangeEditKey(event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey'>) {
  if (event.key === 'Delete' || event.key === 'Backspace') {
    return true;
  }
  return Boolean(event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd';
}

function isMultiCellRange(ranges: CellRange[] | null | undefined) {
  const cellCount = (ranges ?? []).reduce((count, range) => {
    if (!range.startRow || !range.endRow) {
      return count;
    }
    const rowCount = Math.abs(range.endRow.rowIndex - range.startRow.rowIndex) + 1;
    return count + rowCount * range.columns.length;
  }, 0);
  return cellCount > 1;
}

const plainDatePattern = /^\d{4}-\d{2}-\d{2}$/;

/** Copies a date cell as the plain date the BE stores, so it pastes back; every other value goes out raw. */
export function financialsCellToClipboard(params: ProcessCellForExportParams<FinancialsGridRowData>) {
  const { value } = params;
  if (!(value instanceof Date) || isInvalidDate(value)) {
    return value;
  }
  // typed dates are UTC-midnight Dates (kpiStringToTypedValue) and the date formatter shows them in UTC
  return value.toISOString().slice(0, 10);
}

/**
 * Single-cell paste only, until the BE has a bulk upsert. Pasting into a multi-cell selection is
 * cancelled outright: ag-grid would otherwise broadcast a single value into every selected cell.
 * The text must suit the KPI: a number for a numeric KPI (unparseable text would be saved as null and
 * clear the cell), a plain `YYYY-MM-DD` for a date KPI (the only form the BE accepts).
 */
export function restrictPasteToSingleCell(
  params: ProcessDataFromClipboardParams<FinancialsGridRowData>
): string[][] | null {
  if (isMultiCellRange(params.api.getCellRanges())) {
    return null;
  }
  const first = params.data[0]?.[0]?.trim();
  if (first == null || !suitsFocusedCell(params.api, first)) {
    return null;
  }
  return [[first]];
}

function suitsFocusedCell(api: GridApi<FinancialsGridRowData>, text: string) {
  const focused = api.getFocusedCell();
  const renderer = focused
    ? api.getDisplayedRowAtIndex(focused.rowIndex)?.data?.formField.renderer
    : undefined;
  if (!renderer || text === '') {
    return true;
  }
  if (renderer === RendererType.date) {
    return plainDatePattern.test(text);
  }
  return !isNumericRenderer(renderer) || ParserService.get().parse(StandardParserId.Number, text) != null;
}

export function findColumnClosestToDate(columns: Column[], now: number): Column | null {
  let closest: { column: Column; distance: number } | null = null;
  for (const column of columns) {
    const field = column.getColDef().field;
    if (column.isPinned() || !field || !plainDatePattern.test(field)) {
      continue;
    }
    const distance = Math.abs(plainDateToZonedDateTime(field).getTime() - now);
    if (closest === null || distance < closest.distance) {
      closest = { column, distance };
    }
  }
  return closest?.column ?? null;
}

/** Legacy rule (`KpiEditor`): an actual cannot be entered before its period has ended; a budget can. */
function isFutureActualCell(
  row: Pick<FinancialsGridRowData, 'section'> | undefined,
  isoDate: string | undefined,
  now: number
) {
  if (!row || !isoDate || row.section !== KpiSection.actual) {
    return false;
  }
  return endOfDay(plainDateToZonedDateTime(isoDate)).getTime() > now;
}

/**
 * The Company Profile tab's rule. The cell's financial is resolved through its column key, so a pinned year
 * reads the year's item rather than the row's own period; a derived row (LTM, YTD) is read-only throughout.
 */
function companyProfileCellEditable(financialsByKey: Record<string, unknown>) {
  return (row: FinancialsGridRowData, field: string) => {
    const key = resolveCellKey(field, row);
    if (!key || !isKpiPeriod(row.period)) {
      return false;
    }
    return (
      getFinancialAt(financialsByKey, cellRow(row, key), key.date)?.calculated !== true &&
      !isFutureActualCell(row, key.date, Date.now())
    );
  };
}

export interface ICompanyProfileFinancialsColDefsParams {
  headerData: FinancialsHeaderData[];
  financialsByKey: Record<string, unknown>;
  hasAuditorAccess: boolean;
  isNativeCurrency: boolean;
  kpisById: Map<number, IField<unknown>>;
  formatterOverrides: Record<string, string>;
  selectedCurrency: string;
  showGrowth: boolean;
}

/** The actual row's delta for a cell: the BE's `Growth<period>` item of the cell's own period, dated like it. */
function growthLookup({
  financialsByKey,
  kpisById,
}: Pick<ICompanyProfileFinancialsColDefsParams, 'financialsByKey' | 'kpisById'>) {
  // ag-grid asks the renderer again for every row scrolled into view, so each cell's delta is typed only once
  const deltas = new Map<string, number | null>();
  return (row: FinancialsGridRowData, field: string): number | null => {
    const key = resolveCellKey(field, row);
    if (row.kind !== KpiSection.actual || !key || !isKpiPeriod(key.period)) {
      return null;
    }
    // the resolved key, not the column, since a pinned year column reads a different period per row
    const cacheKey = `${row.kpiId}|${key.period}|${key.date}`;
    const cached = deltas.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }
    const growth = { kpiId: row.kpiId, period: periodToGrowth[key.period], section: KpiSection.actual };
    const item = getFinancialAt(financialsByKey, growth, key.date);
    const value = item ? financialToTypedFinancial(item, kpisById)?.value : undefined;
    // a value that did not parse is NaN, and the chip would show it as a meaningless "NA" beside the number
    const delta = typeof value === 'number' && Number.isFinite(value) ? value : null;
    deltas.set(cacheKey, delta);
    return delta;
  };
}

/**
 * The period columns: the shared defs with the tab's guards, formatting, delta chip and range-edit limits on top.
 * The KPI column is the tree's auto group column (`getCompanyProfileKpiTreeColDef`), not part of this list.
 */
export function getCompanyProfileFinancialsColDefs({
  headerData,
  financialsByKey,
  hasAuditorAccess,
  isNativeCurrency,
  kpisById,
  formatterOverrides,
  selectedCurrency,
  showGrowth,
}: ICompanyProfileFinancialsColDefsParams): ColDef<FinancialsGridRowData>[] {
  const formatValue = (data: FinancialsGridRowData | undefined, value: unknown): string => {
    if (value == null || !data) {
      return '';
    }
    const kpi = kpisById.get(data.kpiId);
    if (!kpi) {
      return String(value);
    }
    const overrideId = formatterOverrides[kpi.entityField];
    const formatter = overrideId
      ? FMT.get().getFormatterForId(overrideId)
      : FMT.get().getFormatterForModel(getCurrencyAwareFormatterModel(kpi, selectedCurrency));
    return formatter(value) ?? '';
  };
  const isCurrencyUnresolved = (row: FinancialsGridRowData, field: string) => {
    const key = resolveCellKey(field, row);
    return key
      ? getFinancialAt(financialsByKey, cellRow(row, key), key.date)?.currencyUnresolved === true
      : false;
  };
  const getGrowth = showGrowth ? growthLookup({ financialsByKey, kpisById }) : undefined;
  // An auditor and a converted view are the whole grid's answer, so they gate it rather than every cell.
  const edit: IFinancialsEditPolicy = {
    enabled: !hasAuditorAccess && isNativeCurrency,
    isCellEditable: companyProfileCellEditable(financialsByKey),
  };
  /**
   * Legacy said this from inside the editor, on the disabled field; here the cell never opens one, so a
   * calculated cell would just refuse the click in silence. Only where the cell would otherwise be editable:
   * a derived row or a read-only grid is blocked for its own reason, and this would be the wrong answer.
   */
  const calculatedTooltip = (row: FinancialsGridRowData | undefined, field: string) => {
    if (!row || !edit.enabled || !isKpiPeriod(row.period)) {
      return '';
    }
    const key = resolveCellKey(field, row);
    // an unresolved currency draws its own dash and title; a second tooltip would only crowd it
    if (!key || isCurrencyUnresolved(row, field)) {
      return '';
    }
    return getFinancialAt(financialsByKey, cellRow(row, key), key.date)?.calculated === true
      ? calculatedDescription
      : '';
  };

  return getFinancialsColumnDefs(headerData, edit).map((def) => {
    // the column's own callback, so a header's `editable: false` is not lost under the rules layered on here
    const isEditable = def.editable as (params: { data?: FinancialsGridRowData }) => boolean;
    const cellClasses = isPinnedYearField(def.field)
      ? [...numericCellClasses, PINNED_YEAR_CELL_CLASS]
      : numericCellClasses;
    return {
      ...def,
      // the tab's default col def enables row grouping for the legacy grid; under tree data it would only hide the column
      enableRowGroup: false,
      // a group row has no data and nothing to edit: leave it unpainted rather than grey
      cellClass: (params) =>
        !params.data || isEditable(params) ? cellClasses : [nonEditableCellClass, ...cellClasses],
      // copy carries the raw value, so it pastes back as a number and lands in Excel as one
      useValueFormatterForExport: false,
      valueFormatter: (params) => formatValue(params.data, params.value),
      cellRenderer: FinancialsValueRenderer,
      cellRendererParams: { isCurrencyUnresolved, getGrowth },
      tooltipValueGetter: (params) => calculatedTooltip(params.data, def.field!),
      suppressKeyboardEvent: (params) =>
        !params.editing && isRangeEditKey(params.event) && isMultiCellRange(params.api.getCellRanges()),
    };
  });
}
